import React, { useMemo, useState } from 'react';
import './ShortcutsModalContent.css';
import { getPrimaryModifierKey } from '../../../../utilities/platform';

const BASE_SHORTCUT_TABS = [
  {
    id: 'editor',
    label: 'Editor',
    sections: [
      {
        id: 'general',
        title: 'Navegación',
        items: [
          {
            label: 'Deshacer',
            description: 'Revierte la última acción realizada en el lienzo.',
            keys: ['Ctrl / Cmd', 'Z'],
          },
          {
            label: 'Guardar',
            description: 'Guarda el proyecto actual.',
            keys: ['Ctrl / Cmd', 'G'],
          },
          {
            label: 'Seleccionar todo',
            description: 'Selecciona todos los componentes del diagrama.',
            keys: ['Ctrl / Cmd', 'A'],
          },
          {
            label: 'Buscar símbolo',
            description: 'Activa la búsqueda contextual bajo el cursor.',
            keys: ['Enter'],
          },
          {
            label: 'Insertar símbolo sugerido',
            description: 'Coloca el primer resultado bajo el cursor.',
            keys: ['Enter', 'Enter'],
          },
          {
            label: 'Insertar símbolo sugerido con número',
            description: 'Presiona la posición que se encuentra el símbolo en el buscador.',
            keys: ['Enter', '1/9'],
          },
        ],
      },
      {
        id: 'editing',
        title: 'Edición rápida',
        items: [
          {
            label: 'Deseleccionar',
            description: 'Limpia la selección.',
            keys: ['Esc'],
          },
          {
            label: 'Eliminar selección',
            description: 'Quita nodos y conexiones elegidas del diagrama.',
            keys: ['Supr'],
          },
          {
            label: 'Rotar selección',
            description: 'Alterna la orientación de los nodos seleccionados.',
            keys: ['R'],
          },
        ],
      },
    ],
  },
  {
    id: 'inspector',
    label: 'Inspector',
    sections: [
      {
        id: 'fields',
        title: 'Navegación de campos',
        items: [
          {
            label: 'Inspector',
            description: 'Abrir y cerrar el inspector',
            keys: ['i'],
          },
        ],
      },
    ],
  },
];

const ShortcutKeys = ({ keys }) => (
  <div className="shortcut-modal__keys">
    {keys.map((key, index) => (
      <React.Fragment key={`${key}-${index}`}>
        <span className="shortcut-modal__key">{key}</span>
        {index < keys.length - 1 ? <span className="shortcut-modal__keys-plus">+</span> : null}
      </React.Fragment>
    ))}
  </div>
);

const ShortcutRow = ({ item }) => (
  <div className="shortcut-modal__row">
    <div className="shortcut-modal__row-icon" aria-hidden="true" />
    <div className="shortcut-modal__row-body">
      <div className="shortcut-modal__row-label">{item.label}</div>
      {item.description ? <p className="shortcut-modal__row-desc">{item.description}</p> : null}
    </div>
    <ShortcutKeys keys={item.keys} />
  </div>
);

function ShortcutsModalContent({ onClose }) {
  const [activeTab, setActiveTab] = useState(BASE_SHORTCUT_TABS[0].id);
  const primaryModifierKey = useMemo(() => getPrimaryModifierKey(), []);

  const SHORTCUT_TABS = useMemo(() => {
    const substituteKey = (key) => {
      if (typeof key !== 'string') return key;
      return key.replace(/Ctrl\s*\/\s*Cmd/gi, primaryModifierKey);
    };
    return BASE_SHORTCUT_TABS.map((tab) => ({
      ...tab,
      sections: tab.sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
          ...item,
          keys: item.keys.map((key) => substituteKey(key)),
        })),
      })),
    }));
  }, [primaryModifierKey]);

  const activeTabData = useMemo(
    () => SHORTCUT_TABS.find((tab) => tab.id === activeTab),
    [SHORTCUT_TABS, activeTab],
  );

  if (!activeTabData) {
    return null;
  }

  return (
    <div className="shortcut-modal">
      <header className="shortcut-modal__header">
        <div>
          <h2 className="shortcut-modal__title">Atajos del teclado</h2>
          <p className="shortcut-modal__subtitle">
            Mantén el flujo de trabajo sin soltar el teclado. Cambia de pestaña para ver combinaciones clave.
          </p>
        </div>
        <button type="button" className="shortcut-modal__close" onClick={onClose} aria-label="Cerrar">
          &times;
        </button>
      </header>

      <nav className="shortcut-modal__tabs" aria-label="Categorías de atajos">
        {SHORTCUT_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`shortcut-modal__tab ${tab.id === activeTab ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="shortcut-modal__panel">
        {activeTabData.sections.map((section) => (
          <section key={section.id} className="shortcut-modal__section" aria-labelledby={`shortcut-section-${section.id}`}>
            <h3 id={`shortcut-section-${section.id}`} className="shortcut-modal__section-title">
              {section.title}
            </h3>
            <div className="shortcut-modal__list">
              {section.items.map((item, index) => (
                <ShortcutRow key={`${section.id}-${index}`} item={item} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="shortcut-modal__footer">
        <button type="button" className="shortcut-modal__footer-btn" onClick={onClose}>
          Cerrar
        </button>
      </div>
    </div>
  );
}

export default ShortcutsModalContent;
